import { isFreeTierModelId } from "@winche/shared";

export interface OrModel {
  id: string;
  name: string;
  inputPer1M: number;
  outputPer1M: number;
  supportsVision: boolean;
  isFreeVariant: boolean;
  isReasoning: boolean;
}

interface OrRawModel {
  id: string;
  name: string;
  pricing?: { prompt?: string; completion?: string };
  architecture?: { input_modalities?: string[] };
  supported_parameters?: string[];
}

/**
 * True for models that are reasoning-first by identity (e.g. `*-reasoning` in the id/name).
 * Do NOT use `supported_parameters` — many normal models advertise a `reasoning` param
 * (Nemotron, Gemini, etc.) and that must not hide them from the picker.
 */
export function isReasoningModel(m: { id: string; name?: string }): boolean {
  const id = m.id.toLowerCase();
  const name = (m.name ?? "").toLowerCase();
  return id.includes("reasoning") || /\breasoning\b/.test(name);
}

let _orModelsCache: OrModel[] | null = null;

export async function fetchOrModels(): Promise<OrModel[]> {
  if (_orModelsCache) return _orModelsCache;
  const res = await fetch("https://openrouter.ai/api/v1/models");
  if (!res.ok) throw new Error(`OpenRouter responded with ${res.status}`);
  const data = await res.json() as { data?: OrRawModel[] };
  const raw = data.data ?? [];

  const result: OrModel[] = [];
  for (const m of raw) {
    const inputPer1M  = parseFloat(m.pricing?.prompt     ?? "0") * 1_000_000;
    const outputPer1M = parseFloat(m.pricing?.completion ?? "0") * 1_000_000;
    const modalities  = m.architecture?.input_modalities ?? [];
    const supportsVision = modalities.some(mod => mod === "image" || mod === "file");
    const isFreeVariant = m.id.endsWith(":free") || (m.pricing?.prompt === "0" && m.pricing?.completion === "0");
    const isReasoning = isReasoningModel(m);
    result.push({ id: m.id, name: m.name || m.id, inputPer1M, outputPer1M, supportsVision, isFreeVariant, isReasoning });
  }

  _orModelsCache = result;
  return result;
}

/**
 * A free-tier model: a `:free` slug or OpenRouter's free router
 * (`isFreeTierModelId`, the backend's test), or a price-0 model of the catalog.
 */
export function isFreeModelId(id: string): boolean {
  if (isFreeTierModelId(id)) return true;
  if (_orModelsCache) {
    const cached = _orModelsCache.find(m => m.id === id);
    if (cached) return cached.isFreeVariant;
  }
  return false;
}
